/**
 * Visao geral (spec 053): os numeros do periodo, com a comparacao com o periodo anterior. Cada numero vem de quem e dono
 * do dado: o backend junta o nucleo e os modulos ligados. Bloco de modulo desligado nao chega, entao nao aparece: a tela
 * nao pergunta a marca nem o modulo. O periodo mora na URL.
 *
 * Vitor, 17/10: sem o "Precisa de atenção", e tudo mais compacto ou interativo. Os numeros ficam em faixas (um cartao
 * pequeno por numero, numa caixa so), os blocos lado a lado no desktop, e o detalhe de Etapas e da Taxa de aprovacao
 * abre em abas. Vitor, 20/10: o card de Vendas (faturamento, vendas, ticket e conversao) sai da visao geral; os numeros
 * de venda ficam na tela de Vendas.
 */
import { Abas, Aviso, BotaoLink, CampoData, Carregando, Comparacao, GraficoDeBarras, GraficoDeLinha, Metrica, Metricas, Pilha, Secao, Segmentado, Tabela, formatarReais, hojeEmSaoPaulo } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { erroDe, useApi, type MetricasDoPainel } from "../api";
import { useAmbiente } from "../contexto";
import { ATALHOS, ehAtalho, erroDoPeriodo, periodoDoAtalho, rotuloDaChave, rotuloDoPeriodo, type Atalho } from "../periodos";
import { useSessao } from "../sessao";

const inteiro = (v: number) => Math.round(v).toLocaleString("pt-BR");
const plural = (n: number, um: string, varios: string) => `${inteiro(n)} ${n === 1 ? um : varios}`;
const porcento = (t: number) => `${Math.round(t * 100)}%`;
const porQuando = (a: "dia" | "mes") => (a === "dia" ? "dia" : "mês");
const ALTURA = 160;
const nota = { margin: 0, fontSize: "var(--t-xs)" } as const;

type Etapas = NonNullable<MetricasDoPainel["etapas"]>;

export function VisaoGeral({ modulos, carregando }: { modulos: string[]; carregando?: boolean }) {
  const { estado } = useSessao();
  const { ler } = useApi();
  const { ambiente } = useAmbiente();
  const [params, setParams] = useSearchParams();
  const nome = estado.status === "logado" ? estado.usuario.nome.split(" ")[0] : "";
  const hoje = hojeEmSaoPaulo();
  const atalho: Atalho = ehAtalho(params.get("periodo")) ? (params.get("periodo") as Atalho) : "mes-atual";
  const escolhido = { de: params.get("de") ?? `${hoje.slice(0, 8)}01`, ate: params.get("ate") ?? hoje };
  const periodo = atalho === "datas" ? escolhido : periodoDoAtalho(atalho, hoje);
  const erro = erroDoPeriodo(periodo);
  const mudar = (o: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(o)) if (v == null) p.delete(k); else p.set(k, v);
    setParams(p, { replace: true });
  };
  // Ao abrir "Escolher datas", as datas comecam no periodo que estava na tela.
  const escolherAtalho = (a: Atalho) => mudar(a === "datas" ? { periodo: a, de: periodo.de, ate: periodo.ate } : { periodo: a === "mes-atual" ? null : a, de: null, ate: null });

  const q = new URLSearchParams(periodo).toString();
  const painel = useQuery({ queryKey: ["metricas", "painel", q], queryFn: () => ler<MetricasDoPainel>(`/v1/admin/painel/metricas?${q}`), enabled: !erro, placeholderData: (a) => a });
  const m = painel.data;
  const ambienteEscrito = ambiente === "producao" ? "produção" : ambiente === "homologacao" ? "homologação" : "local";

  return (
    <Pilha espaco={5}>
      <header style={{ display: "grid", gap: "var(--e-2)" }}>
        <h1 className="mtc-titulo mtc-cabecalho__titulo">Olá, {nome}</h1>
      </header>
      <Pilha espaco={2}>
        <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Segmentado rotulo="Período" nome="periodo-da-visao-geral" valor={atalho} aoMudar={escolherAtalho} opcoes={ATALHOS} quebra />
          {atalho === "datas" && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="De" valor={escolhido.de} aoMudar={(de) => mudar({ de })} /></div>}
          {atalho === "datas" && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="Até" valor={escolhido.ate} aoMudar={(ate) => mudar({ ate })} /></div>}
        </div>
        {erro ? <Aviso tipo="alerta">{erro}</Aviso> : m && <p className="mtc-texto-2" style={{ margin: 0 }}>{rotuloDoPeriodo(m.periodo)}, comparado com {rotuloDoPeriodo(m.anterior)}.</p>}
      </Pilha>

      {painel.isError && <Aviso tipo="erro">{erroDe(painel.error)}</Aviso>}
      {!m && painel.isLoading && <Carregando compacto texto="Contando os números do período" />}
      {m && (
        // Lado a lado no desktop (dois por linha), um embaixo do outro no celular; cada bloco com a altura dele.
        <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", alignItems: "start" }}>
          <ClientesEPlanos m={m} />
          {(m.mensalidades || m.etapas) && <Financeiro m={m} />}
          {m.etapas && <BlocoDeEtapas m={m} etapas={m.etapas} />}
          {m.etapas && <BlocoDeAvaliacao etapas={m.etapas} />}
        </div>
      )}
      <p className="mtc-texto-2" style={nota}>Ambiente: <strong>{ambienteEscrito}</strong>. Módulos: {carregando ? "..." : modulos.length ? modulos.join(", ") : "só o núcleo"}.</p>
    </Pilha>
  );
}

/** Itens 7 e 2.4: os planos em andamento (contados pelas inscricoes daqui) e quem nunca entrou. */
function ClientesEPlanos({ m }: { m: MetricasDoPainel }) {
  const p = m.nucleo.planos;
  return (
    <Secao rotulo="Cadastros" titulo="Clientes e planos" acoes={<BotaoLink href="/traders" tamanho="pequeno">Ver os clientes</BotaoLink>}>
      <Metricas rotulo="Clientes e planos">
        <Metrica rotulo="Planos ativos" valor={inteiro(p.ativos)} />
        <Metrica rotulo="Nunca entraram" valor={inteiro(m.nucleo.nunca_entraram)} apoio="Têm plano e nunca abriram a área" tom={m.nucleo.nunca_entraram ? "alerta" : "neutro"} href="/traders?filtro=nunca-entrou" />
        <Metrica rotulo="Aguardando o início" valor={inteiro(p.aguardando_inicio)} />
        <Metrica rotulo="Suspensos" valor={inteiro(p.suspensos)} />
      </Metricas>
      <GraficoDeBarras rotulo="Planos ativos por plano" itens={p.por_plano.map((x) => ({ chave: x.slug, rotulo: x.nome, valor: x.ativos }))} vazio="Nenhum plano ativo." limite={4} />
    </Secao>
  );
}

/** Item 5 e 2.5: mensalidades (vencidas e a vencer sao de agora; o recebido e do periodo) e os repasses pagos. */
function Financeiro({ m }: { m: MetricasDoPainel }) {
  const x = m.mensalidades;
  const r = m.etapas?.repasses;
  const titulo = x && r ? "Mensalidades e repasses" : x ? "Mensalidades" : "Repasses";
  return (
    <Secao rotulo="Financeiro" titulo={titulo} acoes={x ? <BotaoLink href="/mensalidades" tamanho="pequeno">Ver as vencidas</BotaoLink> : <BotaoLink href="/repasses" tamanho="pequeno">Ver os repasses</BotaoLink>}>
      <Metricas rotulo={titulo}>
        {x && <Metrica rotulo="Vencidas" valor={inteiro(x.vencidas)} apoio={x.vencidas ? `${formatarReais(x.vencidas_centavos)} em atraso` : "Nada em atraso"} tom={x.vencidas ? "erro" : "neutro"} />}
        {x && <Metrica rotulo="A vencer em 7 dias" valor={inteiro(x.a_vencer_7_dias)} apoio={x.a_vencer_7_dias ? "Os lembretes saem sozinhos" : "Nada vencendo"} />}
        {x && <Metrica rotulo="Mensalidades recebidas" valor={formatarReais(x.recebido.valor_centavos)} apoio={`${plural(x.recebido.quantidade, "paga", "pagas")} no período`} comparacao={<Comparacao curta atual={x.recebido.valor_centavos} anterior={x.recebido.anterior.valor_centavos} />} />}
        {r && <Metrica rotulo="Repasses pagos" valor={formatarReais(r.valor_centavos)} apoio={`${plural(r.quantidade, "repasse", "repasses")} no período`} comparacao={<Comparacao curta atual={r.valor_centavos} anterior={r.anterior.valor_centavos} />} href="/repasses" />}
      </Metricas>
    </Secao>
  );
}

type AbaDeEtapas = "entradas" | "agora" | "pedidos";

/** Itens 6, 8 e 10: quem entrou em cada etapa (no dia em que a etapa nova passou a valer), onde estao agora e os pedidos. */
function BlocoDeEtapas({ m, etapas }: { m: MetricasDoPainel; etapas: Etapas }) {
  const [aba, setAba] = useState<AbaDeEtapas>("entradas");
  const pedidos = m.nucleo.pedidos_por_tipo;
  const serie = etapas.entradas.serie;
  const abas: { valor: AbaDeEtapas; rotulo: string }[] = [{ valor: "entradas", rotulo: "Entradas" }, { valor: "agora", rotulo: "Agora, por etapa" }, ...(pedidos ? [{ valor: "pedidos" as const, rotulo: "Pedidos" }] : [])];
  return (
    <Secao rotulo="Operação" titulo="Etapas">
      <Metricas rotulo="Entradas nas etapas no período">
        {etapas.entradas.destinos.map((d) => <Metrica key={d.chave} rotulo={d.rotulo} valor={inteiro(d.quantidade)} comparacao={<Comparacao curta atual={d.quantidade} anterior={d.anterior} />} />)}
      </Metricas>
      <Abas rotulo="Detalhe das etapas" valor={aba} aoMudar={setAba} abas={abas} />
      {aba === "entradas" && (
        <GraficoDeLinha
          rotulo={`Entradas nas etapas por ${porQuando(m.agrupamento)}`}
          rotulos={m.chaves.map(rotuloDaChave)}
          series={[
            { nome: "Plataforma liberada", valores: serie.map((s) => s.liberadas) },
            { nome: "Simulador Remunerado", tom: "info", valores: serie.map((s) => s.simulador) },
            { nome: "Mesa Real", tom: "sucesso", valores: serie.map((s) => s.mesa_real) },
          ]}
          formatar={inteiro}
          altura={ALTURA}
          vazio="Ninguém entrou numa etapa no período."
        />
      )}
      {aba === "agora" && <GraficoDeBarras rotulo="Planos em andamento agora, por etapa" itens={etapas.agora.map((e) => ({ chave: e.etapa, rotulo: e.rotulo, valor: e.quantidade }))} tom="info" />}
      {aba === "pedidos" && pedidos && <GraficoDeBarras rotulo="Pedidos recebidos no período, por tipo" itens={pedidos.map((t) => ({ chave: t.tipo, rotulo: t.titulo, valor: t.quantidade, detalhe: <Comparacao curta atual={t.quantidade} anterior={t.anterior} /> }))} vazio="Nenhum pedido no período." />}
    </Secao>
  );
}

type AbaDaTaxa = "mes" | "plano";

/** Itens 12 e 13: a taxa divide pelos que terminaram a avaliacao, nao pelo total de quem comecou. */
function BlocoDeAvaliacao({ etapas }: { etapas: Etapas }) {
  const [aba, setAba] = useState<AbaDaTaxa>("mes");
  const planos = etapas.avaliacao.flatMap((f) => f.por_plano.map((p) => ({ ...p, familia: f.rotulo })));
  const taxa = (t: number | null): ReactNode => (t != null ? porcento(t) : "Sem finalizados");
  return (
    <Secao rotulo="Operação" titulo="Taxa de aprovação">
      <Metricas rotulo="Taxa de aprovação por família">
        {etapas.avaliacao.map((f) => (
          <Metrica key={f.familia} rotulo={f.rotulo} valor={f.taxa != null ? porcento(f.taxa) : "Sem dado"} apoio={`${inteiro(f.aprovados)} de ${plural(f.finalizados, "finalizado", "finalizados")}, ${inteiro(f.em_avaliacao)} em avaliação`} comparacao={<Comparacao curta modo="pontos" atual={f.taxa} anterior={f.anterior.taxa} />} />
        ))}
      </Metricas>
      <Abas rotulo="Detalhe da taxa de aprovação" valor={aba} aoMudar={setAba} abas={[{ valor: "mes", rotulo: "Últimos 12 meses" }, { valor: "plano", rotulo: "Por plano" }]} />
      {aba === "mes" && (
        <GraficoDeLinha
          rotulo="Taxa de aprovação por mês, nos últimos 12 meses"
          rotulos={etapas.taxa_por_mes.map((t) => rotuloDaChave(t.mes))}
          series={[{ nome: "Taxa de aprovação", tom: "sucesso", valores: etapas.taxa_por_mes.map((t) => (t.taxa == null ? null : t.taxa * 100)) }]}
          topo={100}
          formatar={(v) => `${Math.round(v)}%`}
          altura={ALTURA}
          semValor="Ninguém terminou a avaliação"
        />
      )}
      {aba === "plano" && (
        <Tabela
          linhas={planos}
          chave={(p) => p.slug}
          vazio={<p className="mtc-texto-2" style={{ margin: 0 }}>Nenhum plano em avaliação ou finalizado no período.</p>}
          colunas={[
            { chave: "plano", rotulo: "Plano", celula: (p) => <><strong>{p.nome}</strong><span className="mtc-tabela__apoio">{p.familia}</span></> },
            { chave: "avaliando", rotulo: "Avaliando", alinhar: "direita", celula: (p) => <span className="mtc-mono">{inteiro(p.em_avaliacao)}</span> },
            { chave: "aprovados", rotulo: "Aprovados", alinhar: "direita", celula: (p) => <span className="mtc-mono">{inteiro(p.aprovados)}</span> },
            { chave: "reprovados", rotulo: "Reprovados", alinhar: "direita", celula: (p) => <span className="mtc-mono">{inteiro(p.reprovados)}</span> },
            { chave: "taxa", rotulo: "Taxa", alinhar: "direita", celula: (p) => <span className="mtc-mono">{taxa(p.taxa)}</span> },
          ]}
        />
      )}
      <p className="mtc-texto-2" style={nota}>Aprovado é quem saiu da etapa de avaliação (o Teste, ou o Simulador no Fast). A taxa divide pelos que terminaram a avaliação no período; quem ainda está avaliando não entra.</p>
    </Secao>
  );
}
